import { useMemo } from 'react';

import { useQuery } from 'react-query';

import featuresApi from 'api/rest/features/features';
import usersApi from 'api/rest/users/users';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import QUERY from 'constants/queryKeys';

type UseOnGoAccessOptions = {
  /** When true, refetches features on window focus (for dashboard). */
  refetchOnWindowFocus?: boolean;
};

type UseOnGoAccessResult = {
  hasPremiumAccess: boolean;
  /** Whether the user may start or continue using On Go (premium bypasses daily limit). */
  canUseOnGo: boolean;
  /** Free user has exhausted the daily On Go limit according to `GET /api/v1/features/`. */
  isDailyLimitReached: boolean;
  isLoading: boolean;
  isFeaturesFetching: boolean;
  refetchFeatures: () => void;
};

/**
 * Resolves On Go availability for the current user.
 * Premium accounts always pass; free accounts depend on `GET /api/v1/features/` (`on_go`).
 */
const useOnGoAccess = (options: UseOnGoAccessOptions = {}): UseOnGoAccessResult => {
  const { refetchOnWindowFocus = false } = options;

  const { data: meData, isLoading: isMeLoading } = useQuery(QUERY.GET_ME, usersApi.getMe(), {
    refetchOnWindowFocus: false,
  });

  const hasPremiumAccess = Boolean(meData?.hasPremiumAccess);

  const {
    data: features,
    isLoading: isFeaturesLoading,
    isFetching: isFeaturesFetching,
    refetch: refetchFeatures,
  } = useQuery(QUERY.GET_FEATURES, featuresApi.get(), {
    enabled: !isMeLoading && !hasPremiumAccess,
    refetchOnWindowFocus,
  });

  const isOnGoEnabledInFeatures = features?.[FEATURE_FLAGS.ON_GO] === true;

  const canUseOnGo = useMemo(() => {
    if (hasPremiumAccess) {
      return true;
    }
    if (isFeaturesLoading || features == null) {
      return true;
    }
    return isOnGoEnabledInFeatures;
  }, [features, hasPremiumAccess, isFeaturesLoading, isOnGoEnabledInFeatures]);

  const isDailyLimitReached = useMemo(() => {
    if (hasPremiumAccess || features == null) {
      return false;
    }
    return features[FEATURE_FLAGS.ON_GO] === false;
  }, [features, hasPremiumAccess]);

  const isLoading = isMeLoading || (!hasPremiumAccess && isFeaturesLoading);
  const isFeaturesFetchingForUser = !hasPremiumAccess && isFeaturesFetching;

  return {
    hasPremiumAccess,
    canUseOnGo,
    isDailyLimitReached,
    isLoading,
    isFeaturesFetching: isFeaturesFetchingForUser,
    refetchFeatures,
  };
};

export default useOnGoAccess;
